import queryString from 'query-string';

import { ACH_ESRI_ATTRIBUTES_URL, ACH_ESRI_DMAS_URL, ACH_ESRI_GENERATE_URL } from '../../constants/ach/apiUrls';
import { achGetJSON, achSendJSON } from './client';

// ESRI location-file builder (ADOPS-2773). Types follow the ACH wire DTOs in
// internal/transport/http/esri (inputs.go / map.go). /generate is a read-only
// BigQuery query: it returns one geofence row per over-indexing census block
// group, and attaching them to an ad group is the caller's job (see
// esriLocationCsv + the Ventura batch_create upload).

export type EsriCategory =
  | 'race'
  | 'age'
  | 'gender'
  | 'child'
  | 'education'
  | 'HHI'
  | 'familysize'
  | 'market'
  | 'demographic';

export interface EsriAttribute {
  attribute_name: string;
  description: string;
  // Absent on attributes that come from AI Audience search, which has no category.
  category?: EsriCategory;
}

export interface EsriRegions {
  states: string[]; // 2-letter codes
  dmas: number[]; // Nielsen dma_id
  zips: string[]; // 5-digit
}

export type EsriBoundedBy = '' | 'zipcode' | 'state' | 'dma';

export interface StateOption {
  code: string;
  name: string;
}

export interface EsriDma {
  dma_id: number;
  name: string;
}

export interface EsriGeneratedRow {
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
  zip: string | null;
  street: string;
  location_type: string;
  state: string | null;
  city: string;
  dma: string | null;
}

export interface EsriGenerateRequest {
  attributes: EsriAttribute[];
  regions: EsriRegions;
  boundedBy: EsriBoundedBy;
  namePrefix: string;
}

export interface EsriGenerateResponse {
  rows: EsriGeneratedRow[];
  metadata: {
    totalRows: number;
    queryTimeSeconds: number;
    attributeCount: number;
    locationCount: number;
    regionCount: number;
    warning?: string;
  };
}

// achSendJSON folds the response body into the Error message after " — ". ESRI
// contract rejections are 400 problems whose `errors` carry a user-facing
// message, so surface those instead of the raw request dump.
const readableAchError = (err: Error): Error => {
  const [, body] = err.message.split(' — ');
  try {
    const problem = JSON.parse(body);
    const message = problem?.errors?.[0] ?? problem?.detail ?? problem?.title;
    if (message) return new Error(message);
  } catch {
    // not a JSON problem body; fall through
  }
  return err;
};

export const generateEsriLocations = async (
  achApiHost: string,
  { attributes, regions, boundedBy, namePrefix }: EsriGenerateRequest
): Promise<EsriGenerateResponse> => {
  try {
    return await achSendJSON(achApiHost, ACH_ESRI_GENERATE_URL, 'POST', {
      attributes: attributes.map(({ attribute_name, description }) => ({ attribute_name, description })),
      regions,
      boundedBy,
      namePrefix,
    });
  } catch (err) {
    throw readableAchError(err as Error);
  }
};

export const searchEsriAttributes = async (achApiHost: string, q: string): Promise<EsriAttribute[]> => {
  const { attributes } = await achGetJSON(achApiHost, `${ACH_ESRI_ATTRIBUTES_URL}?${queryString.stringify({ q })}`);
  return attributes;
};

export const listEsriDmas = async (achApiHost: string): Promise<EsriDma[]> => {
  const { dmas } = await achGetJSON(achApiHost, ACH_ESRI_DMAS_URL);
  return dmas;
};
